import React, { useMemo } from 'react';

import styles from '../style.module.css';
import { Stack, Tooltip, Typography } from '@mui/material';
import FemaleIcon from '@mui/icons-material/Female';
import MaleIcon from '@mui/icons-material/Male';
import { calculateAge } from '../../../../services/functions';
import { useAccomodationDiagram } from '../AccomodationDiagramContext';
import toast from 'react-hot-toast';
import { baseEventHeight, baseEventWidth } from '../constants';
import { useReactFlow } from '@xyflow/react';
import { v4 as uuidv4 } from 'uuid';

export const BedNode = React.memo(({ id, data, style }) => {
  const { nodes, selectedEventGroup, addNodesF, removeNode,
    setShowOrderDetails, setOrder, setRecalculateHousePositions,
    selectedAccomodationDiagram, events } = useAccomodationDiagram();
  const { getNode } = useReactFlow();
  const node = nodes.filter(n => n.id == id)[0];

  const handleClick = async () => {
    if (!selectedEventGroup) {
      if (data.order) {
        setShowOrderDetails(true);
        setOrder(data.order);
      }

      return;
    }
    const node = getNode(id);
    if (selectedEventGroup.eventRecord.age_range == node.data.ageRangeId) {
      // kliknięte łóżko
      const currentBedNode = nodes.find(n => n.id == id);
      // Sprawdzamy czy istnieje już node z tą imprezą i grupą, jak nie to trzeba go utworzyć
      const existingEventNode = nodes.find(n => n.type == 'EventNode' && n.data.eventId === selectedEventGroup.eventRecord.id && n.data.eventGroup.id === selectedEventGroup.eventGroup.id && currentBedNode.data.houseId == n.data.houseId);

      if (!currentBedNode) {
        return;
      }
      if (currentBedNode.data.eventGroup?.id === selectedEventGroup.eventGroup.id) {
        toast.error('To miejsce jest już przypisane do wybranej grupy');

        return;
      }
      if (currentBedNode.data.order && currentBedNode.data.order.event_id !== selectedEventGroup.eventRecord.id) {
        toast.error('To miejsce jest już zakwaterowane');

        return;
      }
      const takenBeds = nodes.filter(n => n.data?.eventGroup?.id == selectedEventGroup.eventGroup.id && n.type == 'BedNode').length;
      if (selectedEventGroup.eventGroup.slotsCount && takenBeds >= selectedEventGroup.eventGroup.slotsCount) {
        toast.error('Osiągnięto już maksymalną liczbę miejsc dla wybranej grupy imprezowej');

        return;
      }

      // Zbieramy obietnice usuwania i dodawania node'ów, a później użyjemy Promise.all, aby je zgrupować
      const promises = [];
      if (!existingEventNode) {
        const newEventNode = {
          id: `${currentBedNode.id}${uuidv4()}`,
          position: {
            x: baseEventWidth,
            y: 0
          },
          data: {
            houseId: currentBedNode.data.houseId,
            ageRangeNodeDataId: currentBedNode.data.ageRangeNodeDataId,
            ageRangeId: currentBedNode.data.ageRangeId,
            eventId: selectedEventGroup.eventRecord.id,
            eventName: selectedEventGroup.eventRecord.name,
            eventGroup: selectedEventGroup.eventGroup
          },
          style: { width: baseEventWidth, height: baseEventHeight + 10, backgroundColor: selectedEventGroup.eventGroup.color },
          parentId: currentBedNode.data.houseId,
          extent: 'parent',
          className: 'light',
          type: 'EventNode',
          draggable: false

        };

        const currentEventChildrenNodes = nodes.filter(n => n.parentId === currentBedNode.parentId);

        promises.push(removeNode(currentBedNode.id)); // Dodajemy obietnicę usunięcia węzła

        // Jak wykrywamy, że node z tą imprezą ma być pusty, to usuwamy go
        if (currentEventChildrenNodes.length === 1) {
          promises.push(
            new Promise(resolve => setTimeout(() => {
              resolve(removeNode(currentBedNode.parentId));
            }, 50))
          );
        }

        promises.push(
          new Promise(resolve => setTimeout(() => {
            currentBedNode.id = `${currentBedNode.id}${uuidv4()}`;
            currentBedNode.parentId = newEventNode.id;
            currentBedNode.data = { ...currentBedNode.data, eventId: selectedEventGroup.eventRecord.id, eventName: selectedEventGroup.eventRecord.name, eventGroup: selectedEventGroup.eventGroup };
            addNodesF([newEventNode], currentBedNode.data.houseId);
            addNodesF([currentBedNode]);
            resolve();
          }, 50))
        );

      } else {
        const currentEventChildrenNodes = nodes.filter(n => n.parentId === currentBedNode.parentId);
        promises.push(removeNode(currentBedNode.id)); // Usuwamy currentBedNode

        // Jak wykrywamy, że node z tą imprezą ma być pusty, to usuwamy go
        if (currentEventChildrenNodes.length === 1) {
          promises.push(
            new Promise(resolve => setTimeout(() => {
              resolve(removeNode(currentBedNode.parentId));
            }, 50))
          );
        }

        promises.push(
          new Promise(resolve => setTimeout(() => {
            currentBedNode.id = `${currentBedNode.id}${uuidv4()}`;
            currentBedNode.parentId = existingEventNode.id;
            currentBedNode.data = { ...currentBedNode.data, eventId: selectedEventGroup.eventRecord.id, eventName: selectedEventGroup.eventRecord.name, eventGroup: selectedEventGroup.eventGroup };
            addNodesF([currentBedNode]);
            resolve();
          }, 50))
        );
      }

      // Czekamy na wszystkie operacje
      await Promise.all(promises);

      // Teraz wywołujemy setRecalculateHousePositions
      setRecalculateHousePositions([currentBedNode.data.houseId]);

    } else {
      toast.error('Wybrane miejsce nie należy do przedziału wiekowego imprezy');
    }
  };


  const bedStyle = useMemo(() => {
    if (!node) {
      return;
    }
    const bStyle = {};
    if (node.style.backgroundColor) {
      bStyle.backgroundColor = node.style.backgroundColor;
    } else {
      bStyle.backgroundColor = '#FFF';
    }
    if (node.data.order?.relatedGroup) {
      bStyle.border = `4px solid ${node.data.order.relatedGroup.color}`;
    } else {
      Reflect.deleteProperty(bStyle, 'border');
    }
    if (selectedEventGroup && selectedEventGroup.eventRecord) {
      bStyle.border = selectedEventGroup.eventRecord.age_range === node.data.ageRangeId &&
      (!node.data.order || node.data.order.event_id === selectedEventGroup.eventRecord.id)
        ? '1px solid #00ff40'
        : '1px solid #ff0072';
    }

    return bStyle;
  }, [selectedEventGroup, node]);

  const participantName = useMemo(() => data.order?.participant_name ?? '', [data.order]);
  const eventName = useMemo(() => data.eventName?.length > 22 ? `${data.eventName.slice(0, 22)}...` : data.eventName, [data.eventName]);

  const groupColor = useMemo(() => {
    const event = events.find(e => e.groups.some(g => g.id === data.order?.accommodation_event_group));

    return event?.groups.find(g => g.id === data.order?.accommodation_event_group)?.color;
  }, [events, data.order]);

  return (
    <div
      className={styles.bedNode}
      id={data.id}
      onClick={handleClick}
      style={bedStyle}
    >
      <Stack direction="row" alignItems="center" justifyContent="left">
        <Stack direction="row" alignItems="center">
          <Typography variant="body3">{data.slot ? data.slot : ''}</Typography>
          {/* <Iconify icon="lucide:bed" width={14} height={14} /> */}
          {data.order && data.order.shared_accommodation_changed && <Typography color="error">!</Typography>}
          {data.order && !['CON', 'RES'].includes(data.order.state) && <Typography color="orange">!</Typography>}
        </Stack>
        {data.order && <Tooltip title={data.order.event__age_range__name} sx={{ ml: 1 }}>
          <Stack direction="row">
            {data.order.participant_gender == 'boy' ? <MaleIcon sx={{ width: 10, height: 10 }} /> : <FemaleIcon sx={{ width: 10, height: 10 }} />}
            <Stack direction="column">
              <Stack direction="row" alignItems="center" spacing={0.5}>
                {data.order && <Typography color="error" variant="body3">{data.order.accommodation_prefix}</Typography>}
                <Typography color={groupColor} variant={participantName.length < 22 ? 'body3' : 'body4'}>
                  <b>{participantName}</b>
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center">
                <Typography variant="body3"><i>{calculateAge(data.order.participant_birth_date, false, selectedAccomodationDiagram.camp_date_from)}</i>, </Typography>
                <Typography color="default" variant="body3">
                  <i>&nbsp;{eventName}</i>
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </Tooltip>}
      </Stack>
    </div>
  );
});
